import { SSOLoginOrSignupState } from '@dtx-company/inter-app/src/hooks/sso'
import { UseUpdateAuthFormState, useUpdateAuthFormState } from '../../upgrade/UpgradeAuthForm.hooks'
import { useCallback } from 'react'
import { useCreateFlowcodeForm } from '../CreateFlowcodeForm/CreateFlowcodeForm.hooks'
import { useSocialAuthState } from '@dtx-company/inter-app/src/components/singleSignOn/social-auth/index.hooks'

interface SimplifiedDesktopSignUpReturnType
  extends Pick<SSOLoginOrSignupState, 'emailIsSSOEnabled' | 'startSSORedirect' | 'ssoRedirectHtml'>,
    Pick<
      UseUpdateAuthFormState,
      'authenticateUser' | 'control' | 'checkForValidEmail' | 'error' | 'handleSubmit'
    > {
  disabled: boolean
  submitting: boolean
  emailOnChange: (value: string) => void
  handleSocialSignup: ReturnType<typeof useSocialAuthState>['handleClick']
}

type DownloadButtonLabels = {
  buttonLabel: string
  loadingLabel: string
}

export const useDownloadButtonLabels = (emailIsSSOEnabled: boolean): DownloadButtonLabels => {
  let buttonLabel = 'Download'
  let loadingLabel = 'Downloading...'
  if (emailIsSSOEnabled) {
    buttonLabel = 'Sign in with SSO'
    loadingLabel = 'Signing in...'
  }
  return { buttonLabel, loadingLabel }
}

export const useSimplifiedDesktopSignUp = (
  onSuccessLogIn: () => void
): SimplifiedDesktopSignUpReturnType => {
  const {
    isDestinationValidating,
    formState: { isSubmitting }
  } = useCreateFlowcodeForm()

  const {
    checkForValidEmail,
    control,
    isAuthenticating,
    authenticateUser,
    checkIfEmailSSOEnabled,
    emailIsSSOEnabled,
    error,
    handleSubmit,
    ssoRedirectHtml,
    startSSORedirect
  } = useUpdateAuthFormState({
    isSignUp: true,
    onSuccess: onSuccessLogIn
  })

  const { handleClick: handleSocialSignup } = useSocialAuthState({
    onClick: () => onSuccessLogIn()
  })

  const emailOnChange = useCallback(
    (value: string): void => {
      checkIfEmailSSOEnabled(value)
    },
    [checkIfEmailSSOEnabled]
  )

  const disabled = isAuthenticating

  return {
    authenticateUser,
    checkForValidEmail,
    control,
    disabled,
    handleSubmit,
    error,
    submitting: isDestinationValidating || isSubmitting || isAuthenticating,
    emailIsSSOEnabled,
    emailOnChange,
    handleSocialSignup,
    ssoRedirectHtml,
    startSSORedirect
  }
}
